import { useTranslation } from 'react-i18next'
import { useState, useEffect, useMemo } from 'react'
import { Modal, Form, Input, DatePicker, Select, Checkbox } from 'antd'
import type { RangePickerProps } from 'antd/es/date-picker'
import Button from 'components/Button'
import colors from 'globals/colors'
import { Address } from 'globals/models/User'
import { FreeAnswer } from 'globals/models/FreeAnswer'
import { UPSERT_FREE_ANSWER, FREE_ANSWERS } from 'graphql/freeAnswer/queries'
import { useMutation } from '@apollo/client'
import moment, { Moment } from 'moment'
import { ErrorBox, FreeAnswerModalButtonWrapper, WarningFreeAnswer } from './styles'

interface Props {
  createModalOpened: boolean
  setCreateModalOpened: (createModalOpened: boolean) => void
  currentCenters: Address[]
  freeAnswer?: FreeAnswer
}

const UpsertFreeAnswerModal: React.FC<Props> = ({
  createModalOpened,
  setCreateModalOpened,
  currentCenters,
  freeAnswer
}) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [confirmNoAnswer, setConfirmNoAnswer] = useState(false)
  const [upsertFreeAnswer] = useMutation(UPSERT_FREE_ANSWER, {
    refetchQueries: [FREE_ANSWERS]
  })
  const [form] = Form.useForm()

  const SelectOptions = currentCenters.filter((center: Address) => (
    center.externallyManaged
  )).map((center: Address) => ({
    label: center.shortname,
    value: center.id
  }))

  const initialValues = useMemo(
    () => ({
      forwardedAt: moment(freeAnswer?.forwardedAt),
      trackingNumber: freeAnswer?.trackingNumber,
      addressId: freeAnswer?.addressId
    }),
    [freeAnswer]
  )

  const onFinish = (values: {
    addressId: string,
    forwardedAt: Moment,
    trackingNumber: string
  }) => {
    setLoading(true)
    upsertFreeAnswer({
      variables: { ...values, id: freeAnswer?.id }
    }).then(() => {
      setLoading(false)
      setCreateModalOpened(false)
    }).catch((error) => {
      if (error.message.includes('uniqueness_forwarded_at_address_id')) {
        setErrorMessage('uniqueness_forwarded_at_address_id')
      } else if (error.message.includes('uniqueness_tracking_number')) {
        setErrorMessage('uniqueness_tracking_number')
      }
      setLoading(false)
    })
  }

  const disabledDate: RangePickerProps['disabledDate'] = current => {
    return current && current > moment().endOf('day')
  }

  const handleNoAnswerClick = () => {
    if (!confirmNoAnswer) return
    form.validateFields(['addressId', 'forwardedAt']).then((values) => {
      setLoading(true)
      upsertFreeAnswer({
        variables: {
          ...values,
          trackingNumber: null,
          id: freeAnswer?.id
        }
      }).then(() => {
        setLoading(false)
        setCreateModalOpened(false)
      }).catch((error) => {
        if (error.message.includes('uniqueness_forwarded_at_address_id')) {
          setErrorMessage('uniqueness_forwarded_at_address_id')
        } else {
          setErrorMessage(error.message)
        }
        setLoading(false)
      })
    })
  }

  useEffect(() => {
    if (createModalOpened) {
      form.setFieldsValue(initialValues)
    }
  }, [createModalOpened, initialValues])

  useEffect(() => {
    if (!createModalOpened) {
      setConfirmNoAnswer(false)
    }
  }, [createModalOpened])

  return (
    <Modal
      visible={createModalOpened}
      onCancel={() => setCreateModalOpened(false)}
      className='confirm-modal'
      footer={null}
      centered
      closable
      maskClosable
      destroyOnClose
    >
      <Form
        name='leaderForm'
        form={form}
        onFinish={onFinish}
        requiredMark={false}
        layout='vertical'
        preserve={false}
        initialValues={initialValues}
      >
        <Form.Item
          name='forwardedAt'
          label={t('global.forwarded-at')}
          rules={[{ required: true, message: t('global.validation.forwarded-at') }]}
        >
          <DatePicker
            data-testid='forwarded-at-datepicker'
            disabled={!!freeAnswer}
            disabledDate={disabledDate}
          />
        </Form.Item>
        <Form.Item
          name='trackingNumber'
          label={t('global.tracking-number')}
          rules={[{ required: true, message: t('global.validation.tracking-number') }]}
        >
          <Input data-testid='traking-number-input'/>
        </Form.Item>
        <Form.Item
          name='addressId'
          label={t('global.center')}
          rules={[{ required: true, message: t('global.validation.center') }]}
        >
          <Select
            data-testid='address-select'
            options={SelectOptions}
            disabled={!!freeAnswer}
          />
        </Form.Item>
        {
          errorMessage &&
          <ErrorBox>{t(`free-answers.errors.${errorMessage}`)}</ErrorBox>
        }
        {!freeAnswer && (
          <Form.Item>
            <WarningFreeAnswer>
              <Checkbox
                checked={confirmNoAnswer}
                onChange={(e) => setConfirmNoAnswer(e.target.checked)}
                className='confirm-no-answer-checkbox'
              >
                {t('free-answers.no-answer.confirmText')}
              </Checkbox>
            </WarningFreeAnswer>
          </Form.Item>
        )}
        <FreeAnswerModalButtonWrapper>
          <Button
            data-testid='create-free-answer-button'
            color={colors.blueGreen}
            onClick={() => form.submit()}
            loading={loading}
            disabled={confirmNoAnswer}
          >
            {t('global.add')}
          </Button>
          {!freeAnswer && (
            <Button
              data-testid='no-free-answer-button'
              color={colors.reddishPink}
              disabled={!confirmNoAnswer}
              onClick={handleNoAnswerClick}
            >
              {t('free-answers.no-answer.button')}
            </Button>
          )}
        </FreeAnswerModalButtonWrapper>
      </Form>
    </Modal>
  )
}

export default UpsertFreeAnswerModal
